/**
 * The API hands a session over by redirecting the browser to
 * `/auth/token/{access_token}/{refresh_token}` — UpFresh SSO and
 * `/api/auth/login-as-demo` both do. That puts two long-lived credentials in
 * the address bar: roughly a week for the access token, a month for the
 * refresh token.
 *
 * Anything that reads the URL before we clean it therefore gets a usable
 * session. Google Tag Manager is initialised at module scope in
 * `components/App.jsx`, so it — and every tag in the container, including the
 * Smartlook recorder — used to see both tokens. Observed 2026-08-26: a
 * `page_view` hit to `region1.google-analytics.com` carried the whole
 * hand-off URL in `dr`, and would carry it in `dl` whenever the container
 * loads before the app routes away.
 *
 * This module removes the tokens from the URL before any of that can happen,
 * which is why `src/index.js` imports it **first**. Module bodies run in
 * import order, so "first" is literal: move that import below
 * `./components/App` and the leak comes back silently.
 * `src/security/ssoHandoff.test.js` pins the ordering.
 */

/** Where the hand-off route lives once the tokens have been taken out of it. */
export const HANDOFF_PATH = '/auth/token';

const HANDOFF_URL = /^\/auth\/token\/([^/]+)\/([^/]+)\/?$/;

const decode = (segment) => {
    try {
        return decodeURIComponent(segment);
    } catch (error) {
        // A malformed escape is not worth throwing over during app start-up;
        // the raw segment is what the router would have handed us anyway.
        return segment;
    }
};

/**
 * True while the URL still carries the tokens. Analytics must not read a URL
 * for which this holds.
 */
export const isHandoffUrl = (pathname) => HANDOFF_URL.test(pathname);

export const readHandoffTokens = (pathname) => {
    const match = HANDOFF_URL.exec(pathname);

    return match
        ? { access_token: decode(match[1]), refresh_token: decode(match[2]) }
        : null;
};

/**
 * Moves the tokens out of the URL and into storage. Returns them, or null when
 * the current URL is not a hand-off.
 */
export const scrubHandoffUrl = () => {
    const tokens = readHandoffTokens(window.location.pathname);

    if (!tokens) {
        return null;
    }

    // Store before rewriting: CacheBuster hard-reloads the page on a first
    // visit and after every version bump, and until this runs the URL is the
    // only copy of these tokens. The saga writes them again, harmlessly.
    localStorage.token = tokens.access_token;
    localStorage.refreshToken = tokens.refresh_token;

    window.history.replaceState(
        null,
        '',
        `${HANDOFF_PATH}${window.location.search}${window.location.hash}`
    );

    return tokens;
};

scrubHandoffUrl();
